import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  isAfter,
  isBefore,
} from 'date-fns';
import { z } from 'zod';
import {
  isPayAsBidTwoBiddingRounds,
  storageAllocationMethodValues,
  storageCapacityCategoryValues,
  storageCommodityUnitValues,
  storageFlowUnitValues,
  storagePriceTimeUnitValues,
  storageProductPeriodValues,
  storageProductTypeValues,
} from 'src/apis/storage/mappings';
import type {
  StorageAllocationMethod,
  StorageCommodityUnit,
  StorageDownloadFileWrite,
  StorageFlowUnit,
  StorageOfferWriteData,
  StorageProductPeriod,
  StorageSbuUnit,
} from 'src/apis/storage/types';
import { searchableSingleSelectSchema } from 'src/components/form/select/searchable-single-select';
import {
  intParams,
  preprocessAsArray,
  requiredOutput,
  requiredOutputWhen,
} from 'src/components/form/zod-utilities';
import type { Currency } from 'src/utils/currencies';

const STORAGE_OFFER_CURRENCY: Currency = 'EUR';

const optionalAmountSchema = z
  .number()
  .min(0, '{label} must be greater than or equal to 0.')
  .nullable()
  .optional();

const attachedFileSchema = z.object({
  file: z
    .object({
      id: z.string(),
      fileName: z.string(),
    })
    .nullable(),
});

const productComponentSchema = ({
  productRuntimeStart,
  productRuntimeEnd,
}: {
  productRuntimeStart: string | null;
  productRuntimeEnd: string | null;
}) =>
  z
    .object({
      id: z.string(),
      capacityCategory: z
        .enum(storageCapacityCategoryValues)
        .nullable()
        .transform(requiredOutput()),
      runtimeStart: z
        .string()
        .refine(
          (value) =>
            productRuntimeStart
              ? !isBefore(new Date(value), new Date(productRuntimeStart))
              : true,
          'Please enter a {label} date that is after or equal to the Product Runtime Start date.'
        )
        .refine(
          (value) =>
            productRuntimeEnd
              ? isBefore(new Date(value), new Date(productRuntimeEnd))
              : true,
          'Please enter a {label} date that is before the Product Runtime End date.'
        )
        .nullable()
        .transform(
          requiredOutput({ message: 'Please enter a value for {label}.' })
        ),
      runtimeEnd: z
        .string()
        .refine(
          (value) =>
            productRuntimeEnd
              ? !isAfter(new Date(value), new Date(productRuntimeEnd))
              : true,
          'Please enter a {label} date that is before or equal to the Product Runtime End date.'
        )
        .nullable()
        .transform(
          requiredOutput({ message: 'Please enter a value for {label}.' })
        ),
      storageVolumeAmount: optionalAmountSchema,
      storageVolumeUnit: z
        .enum(storageCommodityUnitValues)
        .nullable()
        .optional(),
      injectionCapacityAmount: optionalAmountSchema,
      injectionCapacityUnit: z
        .enum(storageFlowUnitValues)
        .nullable()
        .optional(),
      withdrawalCapacityAmount: optionalAmountSchema,
      withdrawalCapacityUnit: z
        .enum(storageFlowUnitValues)
        .nullable()
        .optional(),
    })
    .superRefine((comp, ctx) => {
      if (
        comp.runtimeStart &&
        comp.runtimeEnd &&
        !isAfter(new Date(comp.runtimeEnd), new Date(comp.runtimeStart))
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Please enter a {label} date that is after the Runtime Start date.',
          path: ['runtimeEnd'],
        });
      }
    });

export type ProductComponentSchema = z.input<
  ReturnType<typeof productComponentSchema>
>;

export const getStorageOfferSchema = ({
  now,
  allowPastPublication = false,
  publication,
  biddingWindowStart,
  biddingWindowEnd,
  productRuntimeStart,
  productRuntimeEnd,
  productPeriod,
  allocationMethod,
  biddingWindowDurationHours,
  biddingWindowDurationMinutes,
  timeBetweenBiddingWindowsHour,
  timeBetweenBiddingWindowsMinute,
}: {
  now: number;
  allowPastPublication?: boolean;
  publication: string | null;
  biddingWindowStart: string | null;
  biddingWindowEnd: string | null;
  productRuntimeStart: string | null;
  productRuntimeEnd: string | null;
  productPeriod: StorageProductPeriod | null;
  allocationMethod: StorageAllocationMethod | null;
  biddingWindowDurationHours: number | null;
  biddingWindowDurationMinutes: number | null;
  timeBetweenBiddingWindowsHour: number | null;
  timeBetweenBiddingWindowsMinute: number | null;
}) => {
  const isTwoBiddingRounds = isPayAsBidTwoBiddingRounds(allocationMethod);

  return z.object({
    name: z
      .string()
      .max(255, 'Please enter a {label} that is shorter than 255 characters.')
      .nullable()
      .transform(requiredOutput()),

    storageLocationId: searchableSingleSelectSchema,

    productPeriod: z
      .enum(storageProductPeriodValues)
      .nullable()
      .transform(requiredOutput()),

    publication: z
      .string()
      .refine(
        (value) => allowPastPublication || isAfter(new Date(value), now),
        'Please enter a {label} date that is in the future.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    biddingWindowStart: z
      .string()
      .refine(
        (value) =>
          publication ? isAfter(new Date(value), new Date(publication)) : true,
        'Please enter a {label} date that is after the Offer Publication date.'
      )
      .nullable()
      .transform(
        requiredOutputWhen(!isTwoBiddingRounds, {
          message: 'Please enter a value for {label}.',
        })
      ),

    biddingWindowEnd: z
      .string()
      .refine(
        (value) =>
          biddingWindowStart
            ? isAfter(new Date(value), new Date(biddingWindowStart))
            : true,
        'Please enter a {label} date that is after the Bidding Window Start date.'
      )
      .nullable()
      .transform(
        requiredOutputWhen(!isTwoBiddingRounds, {
          message: 'Please enter a value for {label}.',
        })
      ),

    firstRoundBiddingWindowStart: z
      .string()
      .refine((value) => {
        const chosenStartDate = new Date(value);
        return publication
          ? isAfter(chosenStartDate, new Date(publication))
          : true;
      }, 'Please enter a {label} that is after Offer Publication.')
      .nullable()
      .transform(requiredOutputWhen(isTwoBiddingRounds)),

    biddingWindowDurationHours: z
      .number()
      .min(
        !biddingWindowDurationMinutes ? 1 : 0,
        biddingWindowDurationMinutes === 0
          ? 'Please enter a {label} that is greater than or equal to 1.'
          : 'Please enter a {label} that is greater than or equal to 0.'
      )
      .nullable()
      .transform(
        requiredOutputWhen(
          isTwoBiddingRounds &&
            !biddingWindowDurationHours &&
            typeof biddingWindowDurationHours === 'string' &&
            !biddingWindowDurationMinutes &&
            typeof biddingWindowDurationMinutes === 'string'
        )
      ),

    biddingWindowDurationMinutes: z
      .number()
      .min(
        !biddingWindowDurationHours ? 1 : 0,
        biddingWindowDurationHours === 0
          ? 'Please enter a {label} that is greater than or equal to 1.'
          : 'Please enter a {label} that is greater than or equal to 0.'
      )
      .nullable()
      .transform(requiredOutputWhen(isTwoBiddingRounds)),

    timeBetweenBiddingWindowsHour: z
      .number()
      .min(
        !timeBetweenBiddingWindowsMinute ? 1 : 0,
        timeBetweenBiddingWindowsMinute === 0
          ? 'Please enter a {label} that is greater than or equal to 1.'
          : 'Please enter a {label} that is greater than or equal to 0.'
      )
      .max(23, 'Please enter a {label} that is less than or equal to 23.')
      .nullable()
      .transform(
        requiredOutputWhen(
          isTwoBiddingRounds &&
            !timeBetweenBiddingWindowsHour &&
            typeof timeBetweenBiddingWindowsHour === 'string' &&
            !timeBetweenBiddingWindowsMinute &&
            typeof timeBetweenBiddingWindowsMinute === 'string'
        )
      ),

    timeBetweenBiddingWindowsMinute: z
      .number()
      .min(
        !timeBetweenBiddingWindowsHour ? 1 : 0,
        timeBetweenBiddingWindowsHour === 0
          ? 'Please enter a {label} that is greater than or equal to 1.'
          : 'Please enter a {label} that is greater than or equal to 0.'
      )
      .max(59, 'Please enter a {label} that is less than or equal to 59.')
      .nullable()
      .transform(requiredOutputWhen(isTwoBiddingRounds)),

    productRuntimeStart: z
      .string()
      .refine(
        (value) =>
          publication ? isAfter(new Date(value), new Date(publication)) : true,
        'Please enter a {label} date that is after the Offer Publication date.'
      )
      .refine(
        (value) =>
          biddingWindowEnd
            ? isAfter(new Date(value), new Date(biddingWindowEnd))
            : true,
        'Please enter a {label} date that is after the Bidding Window End date.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    productRuntimeEnd: z
      .string()
      .refine(
        (value) =>
          productRuntimeStart
            ? isAfter(new Date(value), new Date(productRuntimeStart))
            : true,
        'Please enter a {label} date that is after the Product Runtime Start date.'
      )
      .refine((value) => {
        if (
          !productRuntimeStart ||
          !productPeriod ||
          productPeriod === 'CUSTOM'
        )
          return true;
        const start = new Date(productRuntimeStart);
        const end = new Date(value);

        if (productPeriod === 'MULTIYEAR') {
          return isAfter(end, addYears(start, 1));
        }

        const expectedEnd = {
          WITHINDAY: addHours(start, 24),
          DAY: addDays(start, 1),
          WEEK: addWeeks(start, 1),
          MONTH: addMonths(start, 1),
          YEAR: addYears(start, 1),
        }[productPeriod];

        return end.getTime() === expectedEnd.getTime();
      }, `Product Runtime must span exactly 1 ${productPeriod?.toLowerCase()} for this period. Please adjust the runtime or select "Custom" as the Product Period.`)
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    productType: z.enum(storageProductTypeValues),

    typeOfProductUnit: z.string().nullable().transform(requiredOutput()),

    allocationMethod: z
      .enum(storageAllocationMethodValues)
      .nullable()
      .transform(requiredOutput()),

    productName: z
      .string()
      .max(255, 'Please enter a {label} that is shorter than 255 characters.')
      .nullable()
      .transform(requiredOutput()),

    availableQuantity: z
      .number()
      .int(intParams())
      .min(1, '{label} must be greater than or equal to 1.')
      .nullable()
      .transform(requiredOutput()),

    reservePrice: z
      .number()
      .min(0, '{label} must be greater than or equal to 0.')
      .nullable()
      .optional(),

    priceTimeUnit: z.enum(storagePriceTimeUnitValues),

    productComponents: z.preprocess(
      preprocessAsArray,
      z
        .array(
          productComponentSchema({ productRuntimeStart, productRuntimeEnd })
        )
        .superRefine((components, ctx) => {
          components.forEach((comp, index) => {
            if (
              (comp.storageVolumeAmount != null) !==
              (comp.storageVolumeUnit != null)
            ) {
              ctx.addIssue({
                code: 'custom',
                message:
                  comp.storageVolumeUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.storageVolumeUnit == null
                    ? 'storageVolumeUnit'
                    : 'storageVolumeAmount',
                ],
              });
            }
            if (
              (comp.injectionCapacityAmount != null) !==
              (comp.injectionCapacityUnit != null)
            ) {
              ctx.addIssue({
                code: 'custom',
                message:
                  comp.injectionCapacityUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.injectionCapacityUnit == null
                    ? 'injectionCapacityUnit'
                    : 'injectionCapacityAmount',
                ],
              });
            }
            if (
              (comp.withdrawalCapacityAmount != null) !==
              (comp.withdrawalCapacityUnit != null)
            ) {
              ctx.addIssue({
                code: 'custom',
                message:
                  comp.withdrawalCapacityUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.withdrawalCapacityUnit == null
                    ? 'withdrawalCapacityUnit'
                    : 'withdrawalCapacityAmount',
                ],
              });
            }
          });
        })
    ),
    offerSpecificNote: z
      .string()
      .nullable()
      .optional()
      .transform((v) => (v?.trim() ? v : null)),

    attachedFiles: z.array(attachedFileSchema).default([]),
  });
};

type StorageOfferSchema = ReturnType<typeof getStorageOfferSchema>;

export type StorageOfferFormValues = z.input<StorageOfferSchema>;

type ValidStorageOfferFormValues = z.output<StorageOfferSchema>;

function buildTypeOfProduct(
  values: ValidStorageOfferFormValues
): StorageOfferWriteData['typeOfProduct'] {
  const { productType, typeOfProductUnit } = values;
  switch (productType) {
    case 'storageVolume':
      return {
        typeOfProduct: 'STORAGE_VOLUME',
        unit: typeOfProductUnit as StorageCommodityUnit,
      };
    case 'injectionCapacity':
      return {
        typeOfProduct: 'INJECTION_CAPACITY',
        unit: typeOfProductUnit as StorageFlowUnit,
      };
    case 'withdrawalCapacity':
      return {
        typeOfProduct: 'WITHDRAWAL_CAPACITY',
        unit: typeOfProductUnit as StorageFlowUnit,
      };
    case 'SBU':
      return {
        typeOfProduct: 'SBU',
        unit: typeOfProductUnit as StorageSbuUnit,
      };
  }
}

function mapProductComponent(
  comp: ValidStorageOfferFormValues['productComponents'][number]
) {
  const runtime = { start: comp.runtimeStart, end: comp.runtimeEnd };
  const storageVolume =
    comp.storageVolumeAmount != null &&
    comp.storageVolumeAmount >= 0 &&
    comp.storageVolumeUnit
      ? { amount: comp.storageVolumeAmount, unit: comp.storageVolumeUnit }
      : null;
  const injectionCapacity =
    comp.injectionCapacityAmount != null &&
    comp.injectionCapacityAmount >= 0 &&
    comp.injectionCapacityUnit
      ? {
          amount: comp.injectionCapacityAmount,
          unit: comp.injectionCapacityUnit,
        }
      : null;
  const withdrawalCapacity =
    comp.withdrawalCapacityAmount != null &&
    comp.withdrawalCapacityAmount >= 0 &&
    comp.withdrawalCapacityUnit
      ? {
          amount: comp.withdrawalCapacityAmount,
          unit: comp.withdrawalCapacityUnit,
        }
      : null;
  return {
    capacityCategory: comp.capacityCategory,
    runtime,
    storageVolume: storageVolume
      ? { amount: storageVolume.amount, unit: storageVolume.unit }
      : null,
    injectionCapacity: injectionCapacity
      ? { amount: injectionCapacity.amount, unit: injectionCapacity.unit }
      : null,
    withdrawalCapacity: withdrawalCapacity
      ? { amount: withdrawalCapacity.amount, unit: withdrawalCapacity.unit }
      : null,
  };
}

export function formValuesToWriteData(
  values: ValidStorageOfferFormValues
): StorageOfferWriteData {
  const reservePrice =
    values.reservePrice != null
      ? { amount: values.reservePrice, currency: STORAGE_OFFER_CURRENCY }
      : null;

  const attachedFiles: StorageDownloadFileWrite[] = Array.isArray(
    values.attachedFiles
  )
    ? values.attachedFiles
        .filter(
          (item): item is { file: { id: string; fileName: string } } =>
            item.file != null &&
            typeof item.file.id === 'string' &&
            typeof item.file.fileName === 'string'
        )
        .map((item) => ({
          id: item.file.id,
          fileName: item.file.fileName,
        }))
    : [];

  const base = {
    name: values.name,
    storageLocationId: values.storageLocationId.value,
    productRuntime: {
      start: values.productRuntimeStart,
      end: values.productRuntimeEnd,
    },
    publication: values.publication,
    typeOfProduct: buildTypeOfProduct(values),
    allocationMethod: values.allocationMethod,
    productName: values.productName,
    availableQuantity: values.availableQuantity,
    reservePrice,
    priceTimeUnit: values.priceTimeUnit,
    productComponents:
      values.productComponents.length > 0
        ? values.productComponents.map(mapProductComponent)
        : [],
    offerSpecificNote: values.offerSpecificNote,
    productPeriod: values.productPeriod,
    attachedFiles,
  };

  if (isPayAsBidTwoBiddingRounds(values.allocationMethod)) {
    return {
      ...base,
      firstRoundBiddingWindowStart: values.firstRoundBiddingWindowStart!,
      biddingWindowDurationInMinutes:
        (values.biddingWindowDurationHours ?? 0) * 60 +
        (values.biddingWindowDurationMinutes ?? 0),
      timeBetweenBiddingWindowsInMinutes:
        (values.timeBetweenBiddingWindowsHour ?? 0) * 60 +
        (values.timeBetweenBiddingWindowsMinute ?? 0),
    };
  }

  return {
    ...base,
    biddingWindow: {
      start: values.biddingWindowStart!,
      end: values.biddingWindowEnd!,
    },
  };
}
